import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import {
    acquireBrowserAccessTokenAsync,
    acquireNexusBrowserAccessTokenAsync,
    ensureBrowserAuthenticatedSessionAsync,
    loginBrowserAsync,
    logoutBrowserAsync,
} from './BrowserAuthService';
import { resolveAuthEnvironment } from './AuthEnvironmentResolver';
import {
    acquireTeamsAccessTokenAsync,
    acquireNexusTeamsAccessTokenAsync,
    ensureTeamsAuthenticatedSessionAsync,
    loginTeamsAsync,
} from './TeamsAuthService';
import { trackException } from '../../app/telemetry/appInsights';


export interface EnsureAuthenticatedSessionArgs {
    msalInstance: IPublicClientApplication;
}

export interface AcquireApiAccessTokenArgs {
    msalInstance: IPublicClientApplication;
}

export const getAllAccounts = (msalInstance: IPublicClientApplication): AccountInfo[] => {
    return msalInstance.getAllAccounts();
};

export const getActiveAccount = (msalInstance: IPublicClientApplication): AccountInfo | null => {
    const active = msalInstance.getActiveAccount();
    if (active) return active;

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return null;

    msalInstance.setActiveAccount(accounts[0]);
    return accounts[0];
};

export const setActiveAccount = (msalInstance: IPublicClientApplication, account: AccountInfo): void => {
    msalInstance.setActiveAccount(account);
};

export const isAuthenticated = (msalInstance: IPublicClientApplication): boolean => {
    try {
        const active = msalInstance.getActiveAccount();
        if (active?.homeAccountId) return true;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return false;

        // Recover active account deterministically.
        msalInstance.setActiveAccount(accounts[0]);
        return Boolean(accounts[0]?.homeAccountId);
    } catch (error) {
        trackException(error, {
            source: 'authService.isAuthenticated',
        });
        return false;
    }
};

export const ensureAuthenticatedSessionAsync = async ({
    msalInstance,
}: EnsureAuthenticatedSessionArgs): Promise<boolean> => {
    const environment = await resolveAuthEnvironment();

    if (environment.isInTeams) {
        return await ensureTeamsAuthenticatedSessionAsync({ msalInstance });
    }

    return await ensureBrowserAuthenticatedSessionAsync({ msalInstance });
};

export const loginAsync = async ({ msalInstance }: EnsureAuthenticatedSessionArgs): Promise<boolean> => {
    const environment = await resolveAuthEnvironment();

    if (environment.isInTeams) {
        return Boolean(await loginTeamsAsync({ msalInstance }));
    }

    await loginBrowserAsync({ msalInstance });
    return true;
};

export const acquireApiAccessTokenAsync = async ({
    msalInstance,
}: AcquireApiAccessTokenArgs): Promise<string> => {
    const environment = await resolveAuthEnvironment();

    if (environment.isInTeams) {
        return await acquireTeamsAccessTokenAsync({ msalInstance });
    }

    return await acquireBrowserAccessTokenAsync({ msalInstance });
};

export const acquireNexusApiAccessTokenAsync = async ({
    msalInstance,
}: AcquireApiAccessTokenArgs): Promise<string> => {
    const environment = await resolveAuthEnvironment();

    if (environment.isInTeams) {
        return await acquireNexusTeamsAccessTokenAsync({ msalInstance });
    }

    return await acquireNexusBrowserAccessTokenAsync({ msalInstance });
};

export const logoutAsync = async ({ msalInstance }: EnsureAuthenticatedSessionArgs): Promise<void> => {
    const environment = await resolveAuthEnvironment();

    if (environment.isInTeams) {
        return;
    }

    await logoutBrowserAsync({ msalInstance });
};
